import { createApi } from "@reduxjs/toolkit/query/react";
import {
    SkuReportsResult,
    SkuReportsSearch,
    SkuReports
} from "@/types/meesho/meeshoAllCompany";
import { marketPlaceBaseQueryWrapper } from "@/utils/rtkHelper";
import {
    OrderItemSearch,
    OrderItemSearchResult,
    OrderItemSummaryResult,
    MissingStockSearch,
    MissingStockResult,
} from "@/types/meesho/meeshoOrder";
import { UnAssignSkuSearch,UnAssignSkuSearchResult,UnAssignSku } from "@/types/meesho/UnAssignSku";
export const MeeshoAllCompanyApi = createApi({
    reducerPath: "MeeshoAllCompanyApi",
    baseQuery: marketPlaceBaseQueryWrapper,
    keepUnusedDataFor: 0,
    tagTypes: ["orderItem"],
    endpoints: (builder) => ({
        getDashboardCounts: builder.query({
            query: ({ startDate, endDate, skuId }) =>
                `/meesho-all-company/dashboard-count/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
            providesTags: ["orderItem"],
        }),
        getTotalOrdersChart: builder.query({
            query: ({ startDate, endDate, skuId }) =>
                `/meesho-all-company/total-order-chart/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
            providesTags: ["orderItem"],
        }),
        getChargeableAmtChart: builder.query({
            query: ({ startDate, endDate, skuId }) =>
                `/meesho-all-company/chargeable-amounts/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
            providesTags: ["orderItem"],
        }),
        getReturnStatusChartChart: builder.query({
            query: ({ startDate, endDate, skuId }) =>
                `/meesho-all-company/return-status-chart/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
            providesTags: ["orderItem"],
        }),
        getTop10SkuProfitChart: builder.query({
            query: ({ startDate, endDate, skuId }) =>
                `/meesho-all-company/top-10-sku-profit-chart/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
            providesTags: ["orderItem"],
        }),
        getTop10SkuLossChart: builder.query({
            query: ({ startDate, endDate, skuId }) =>
                `/meesho-all-company/top-10-sku-loss-chart/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
            providesTags: ["orderItem"],
        }),
        getTop10SalesSkuChart: builder.query({
            query: ({ startDate, endDate, skuId }) =>
                `/meesho-all-company/top-10-sales-sku/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
            providesTags: ["orderItem"],
        }),
        getTop10StateWiseCustomerReturnChart: builder.query({
            query: ({ startDate, endDate, skuId }) =>
                `/meesho-all-company/top-10-customer-returns-state/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
            providesTags: ["orderItem"],
        }),
        getTop10SkuOfCustomerReturnChart: builder.query({
            query: ({ startDate, endDate, skuId }) =>
                `/meesho-all-company/top-10-sku-of-customer-return/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
            providesTags: ["orderItem"],
        }),
        getTop10CityWiseSalesOrderChart: builder.query({
            query: ({ startDate, endDate, skuId }) =>
                `/meesho-all-company/top-10-sales-order-city-wise/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
            providesTags: ["orderItem"],
        }),
        getTop10StateWiseSalesOrderChart: builder.query({
            query: ({ startDate, endDate, skuId }) =>
                `/meesho-all-company/top-10-sales-order-state-wise/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
            providesTags: ["orderItem"],
        }),
        getTop10CustomerReturnChart: builder.query({
            query: ({ startDate, endDate, skuId }) =>
                `/meesho-all-company/top-10-customer-return/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
            providesTags: ["orderItem"],
        }),
        getRealTimeProfitChart: builder.query({
            query: ({ startDate, endDate, skuId }) =>
                `/meesho-all-company/real-time-profit/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
            providesTags: ["orderItem"],
        }),
        getUpcomingProfitChart: builder.query({
            query: ({ startDate, endDate, skuId }) =>
                `/meesho-all-company/upcoming-profit/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
            providesTags: ["orderItem"],
        }),
        getNetProfitChart: builder.query({
            query: ({ startDate, endDate, skuId }) =>
                `/meesho-all-company/net-profit/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
            providesTags: ["orderItem"],
        }),
        getTotalOrderChartAllCompany: builder.query({
            query: ({ startDate, endDate, skuId }) =>
                `/meesho-all-company/company-total-order-chart/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
            providesTags: ["orderItem"],
        }),
        exportSkuReports: builder.mutation<Blob, SkuReportsSearch>({
            query: (input) => ({
                url: `/meesho-all-company/sku-report`,
                method: "POST",
                body: { ...input, export: true },
                responseHandler: (response: Response) => response.blob(),
            }),
        }),
        skuReportsMeeshoOrders: builder.query<SkuReportsResult, SkuReportsSearch>({
            query: (input) => ({
                url: `/meesho-all-company/sku-report`,
                method: "POST",
                body: { ...input, export: false },
            }),
            providesTags: ["orderItem"],
        }),
        summarySkuReport: builder.query<SkuReports, SkuReportsSearch>({
            query: (input) => ({
                url: `/meesho-all-company/sku-report/summary`,
                method: "POST",
                body: input,
            }),
            providesTags: ["orderItem"],
        }),
        getDropdownOptions: builder.query({
            query: () => `/meesho-all-company/sku/sku-dropdown`,
            providesTags: ["orderItem"],
        }),
        searchMeeshoOrders: builder.query<OrderItemSearchResult, OrderItemSearch>({
            query: (input) => ({
                url: `/meesho-all-company/ordersearch`,
                method: "POST",
                body: { ...input, export: false },
            }),
            providesTags: ["orderItem"],
        }),
        exportMeeshoOrders: builder.mutation<Blob, OrderItemSearch>({
            query: (input) => ({
                url: `/meesho-all-company/ordersearch`,
                method: "POST",
                body: { ...input, export: true },
                responseHandler: (response: Response) => response.blob(),
            }),
        }),
        summaryOrderItem: builder.query<OrderItemSummaryResult, OrderItemSearch>({
            query: (input) => ({
                url: `/meesho-all-company/ordersummary`,
                method: "POST",
                body: input,
            }),
            providesTags: ["orderItem"],
        }),
        getUnAssignSku: builder.query<UnAssignSkuSearchResult, UnAssignSkuSearch>({
            query: (input) => ({
                url: `/meesho-all-company/unassigned-skus`,
                method: "POST",
                body: { ...input, export: false },
            }),
            providesTags: ["orderItem"],
        }),
        exportUnAssignSku: builder.mutation<Blob, UnAssignSkuSearch>({
            query: (input) => ({
                url: `/meesho-all-company/unassigned-skus`,
                method: "POST",
                body: { ...input, export: true },
                responseHandler: (response: Response) => response.blob(),
            }),
        }),




    }),
});
export const {
    useGetDashboardCountsQuery,
    useGetTotalOrdersChartQuery,
    useGetReturnStatusChartChartQuery,
    useGetChargeableAmtChartQuery,
    useGetTop10SkuProfitChartQuery,
    useGetTop10SkuLossChartQuery,
    useGetTop10SalesSkuChartQuery,
    useGetTop10StateWiseCustomerReturnChartQuery,
    useGetTop10SkuOfCustomerReturnChartQuery,
    useGetTop10CityWiseSalesOrderChartQuery,
    useGetTop10StateWiseSalesOrderChartQuery,
    useGetTop10CustomerReturnChartQuery,
    useGetNetProfitChartQuery,
    useGetRealTimeProfitChartQuery,
    useGetUpcomingProfitChartQuery,
    useGetDropdownOptionsQuery,
    useExportSkuReportsMutation,
    useSkuReportsMeeshoOrdersQuery,
    useSummarySkuReportQuery,
    useSummaryOrderItemQuery,
    useExportMeeshoOrdersMutation,
    useSearchMeeshoOrdersQuery,
    useGetTotalOrderChartAllCompanyQuery,
    useGetUnAssignSkuQuery,
    useExportUnAssignSkuMutation,
} = MeeshoAllCompanyApi;